/**
 * TagDetailPage — F031
 *
 * Detailseite für einen Tag mit zugeordneten Hotspots und Contacts.
 */
import { useState } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTag, useUpdateTag, useRemoveHotspotsFromTag, useRemoveContactsFromTag } from './useTags'
import { hotspotDisplayName } from '../../lib/hotspotDisplayName'

// --- Loading State ---

function LoadingSkeleton() {
  const { t } = useTranslation('tags')
  return (
    <div aria-busy="true" aria-label={t('loading.tag')}>
      <div className="skeleton-line" style={{ width: 200, height: 24, marginBottom: 16 }} />
      <div className="skeleton-line" style={{ width: '100%', height: 200 }} />
    </div>
  )
}

// --- Error State ---

function ErrorState({ message, onRetry }: { message?: string; onRetry: () => void }) {
  const { t } = useTranslation('tags')
  return (
    <div className="error-state" role="alert">
      <p>{message || t('error.loadTag')}</p>
      <button className="btn btn-primary" onClick={onRetry}>
        {t('common:actions.retry')}
      </button>
    </div>
  )
}

// --- Hotspot Item ---

type HotspotItemProps = {
  id: number
  name: string
  address: string
  onRemove?: () => void
}

function HotspotItem({ id, name, address, onRemove }: HotspotItemProps) {
  const { t } = useTranslation('tags')
  return (
    <div className="list-row" data-testid={`hotspot-item-${id}`} style={{ gridTemplateColumns: '1fr auto' }}>
      <div className="identity">
        <div className="identity-main">
          <span className="identity-name">{name}</span>
          <span className="identity-meta" style={{ fontSize: 12 }}>{address}</span>
        </div>
      </div>
      {onRemove && (
        <div className="list-actions">
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={onRemove}
            data-testid={`remove-hotspot-${id}`}
          >
            {t('actions.remove')}
          </button>
        </div>
      )}
    </div>
  )
}

// --- Contact Item ---

type ContactItemProps = {
  id: number
  name: string
  email?: string
  onRemove?: () => void
}

function ContactItem({ id, name, email, onRemove }: ContactItemProps) {
  const { t } = useTranslation('tags')
  return (
    <div className="list-row" data-testid={`contact-item-${id}`} style={{ gridTemplateColumns: '1fr auto' }}>
      <div className="identity">
        <div className="identity-main">
          <span className="identity-name">{name}</span>
          {email && <span className="identity-meta" style={{ fontSize: 12 }}>{email}</span>}
        </div>
      </div>
      {onRemove && (
        <div className="list-actions">
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={onRemove}
            data-testid={`remove-contact-${id}`}
          >
            {t('actions.remove')}
          </button>
        </div>
      )}
    </div>
  )
}

// --- Main Page ---

export function TagDetailPage() {
  const { t } = useTranslation('tags')
  const { id } = useParams({ from: '/_auth/tags/$id' })
  const navigate = useNavigate()
  const { data: tag, isLoading, error, refetch } = useTag(id)
  const updateTag = useUpdateTag()
  const removeHotspots = useRemoveHotspotsFromTag()
  const removeContacts = useRemoveContactsFromTag()

  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDashboard, setEditDashboard] = useState(true)

  function startEdit() {
    if (!tag) return
    setEditName(tag.name)
    setEditDashboard(tag.dashboard)
    setIsEditing(true)
  }

  function handleUpdate() {
    if (!tag || !editName.trim()) return
    updateTag.mutate(
      { id: tag.id, name: editName.trim(), dashboard: editDashboard },
      {
        onSuccess: () => {
          setIsEditing(false)
          refetch()
        },
      }
    )
  }

  function handleRemoveHotspot(hotspotId: number) {
    if (!tag) return
    removeHotspots.mutate(
      { tagId: tag.id, hotspotIds: [hotspotId] },
      { onSuccess: () => refetch() }
    )
  }

  function handleRemoveContact(contactId: number) {
    if (!tag) return
    removeContacts.mutate(
      { tagId: tag.id, contactIds: [contactId] },
      { onSuccess: () => refetch() }
    )
  }

  if (isLoading) return <LoadingSkeleton />

  if (error || !tag) {
    return (
      <ErrorState
        message={error?.message}
        onRetry={refetch}
      />
    )
  }

  return (
    <div className="page">
      <div className="page-header">
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => navigate({ to: '/tags' })}
          style={{ marginBottom: 16 }}
        >
          <ArrowLeft size={16} style={{ marginRight: 4 }} />
          {t('detail.backToTags')}
        </button>
        <div>
          <h1>{tag.name}</h1>
          <p className="muted" style={{ marginTop: 4 }}>
            {/* F15 (FIN-SWEEP-FE-7): Singular/Plural via i18n-count-Keys */}
            {t('detail.hotspotsCount', { count: tag.hotspots_count })} · {t('detail.contactsCount', { count: tag.contacts_count })}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={startEdit}
          data-testid="edit-tag-btn"
        >
          {t('common:actions.edit')}
        </button>
      </div>

      {/* Edit Form */}
      {isEditing && (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <h3 style={{ marginBottom: 16 }}>{t('detail.editForm.title')}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label className="label" htmlFor="edit-tag-name">{t('detail.editForm.fieldName')}</label>
              <input
                id="edit-tag-name"
                type="text"
                className="input"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                id="edit-tag-dashboard"
                type="checkbox"
                checked={editDashboard}
                onChange={(e) => setEditDashboard(e.target.checked)}
              />
              <label htmlFor="edit-tag-dashboard" style={{ fontSize: 13, cursor: 'pointer' }}>
                {t('detail.editForm.showOnDashboard')}
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn"
                onClick={() => setIsEditing(false)}
                disabled={updateTag.isPending}
              >
                {t('common:actions.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleUpdate}
                disabled={updateTag.isPending || !editName.trim()}
              >
                {updateTag.isPending ? t('detail.editForm.savingLabel') : t('common:actions.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hotspots Section */}
      <section style={{ marginTop: 24 }}>
        <h2 style={{ marginBottom: 12 }}>{t('detail.hotspots.sectionTitle', { count: tag.hotspots_count })}</h2>
        {tag.hotspots && tag.hotspots.length > 0 ? (
          <div className="list" role="list">
            <div className="list-head" style={{ gridTemplateColumns: '1fr auto' }}>
              <div className="list-cell">{t('detail.hotspots.columnName')}</div>
              <div className="list-actions">{t('detail.hotspots.columnActions')}</div>
            </div>
            {tag.hotspots.map((hotspot) => (
              <HotspotItem
                key={hotspot.id}
                id={hotspot.id}
                address={hotspot.address}
                name={hotspotDisplayName(hotspot.name, hotspot.address, t('empty.unknownHotspot'))}
                onRemove={() => handleRemoveHotspot(hotspot.id)}
              />
            ))}
          </div>
        ) : (
          <p className="muted">{t('empty.noHotspots')}</p>
        )}
      </section>

      {/* Contacts Section */}
      <section style={{ marginTop: 24 }}>
        <h2 style={{ marginBottom: 12 }}>{t('detail.contacts.sectionTitle', { count: tag.contacts_count })}</h2>
        {tag.contacts && tag.contacts.length > 0 ? (
          <div className="list" role="list">
            <div className="list-head" style={{ gridTemplateColumns: '1fr auto' }}>
              <div className="list-cell">{t('detail.contacts.columnName')}</div>
              <div className="list-actions">{t('detail.contacts.columnActions')}</div>
            </div>
            {tag.contacts.map((contact) => (
              <ContactItem
                key={contact.id}
                {...contact}
                onRemove={() => handleRemoveContact(contact.id)}
              />
            ))}
          </div>
        ) : (
          <p className="muted">{t('empty.noContacts')}</p>
        )}
      </section>
    </div>
  )
}